'use client';
import React, { createContext, useContext, useState, useEffect } from 'react';

export interface CompanyInfo {
  _id?: string;
  companyName: string;
  tagline: string;
  logo: {
    light: string;
    dark: string;
    favicon: string;
  };
  contact: {
    email: string;
    phone: string;
    whatsapp: string;
    supportEmail: string;
    salesEmail: string;
  };
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    fullAddress: string;
    mapUrl: string;
  };
  socialMedia: {
    facebook: string;
    twitter: string;
    linkedin: string;
    instagram: string;
    youtube: string;
    github: string;
  };
  businessInfo: {
    establishedYear: number;
    registrationNumber: string;
    taxNumber: string;
    website: string;
    description: string;
  };
  workingHours: {
    monday: { open: string; close: string; isOpen: boolean };
    tuesday: { open: string; close: string; isOpen: boolean };
    wednesday: { open: string; close: string; isOpen: boolean };
    thursday: { open: string; close: string; isOpen: boolean };
    friday: { open: string; close: string; isOpen: boolean };
    saturday: { open: string; close: string; isOpen: boolean };
    sunday: { open: string; close: string; isOpen: boolean };
  };
  additionalInfo: {
    employeeCount: string;
    officeLocations: string[];
    certifications: string[];
    awards: string[];
  };
  seo: {
    metaTitle: string;
    metaDescription: string;
    keywords: string[];
  };
}

interface CompanyInfoContextType {
  companyInfo: CompanyInfo | null;
  loading: boolean;
  updateCompanyInfo: (data: Partial<CompanyInfo>) => Promise<void>;
  refreshCompanyInfo: () => Promise<void>;
}

const CompanyInfoContext = createContext<CompanyInfoContextType | undefined>(undefined);

export const useCompanyInfo = () => {
  const context = useContext(CompanyInfoContext);
  if (!context) {
    throw new Error('useCompanyInfo must be used within a CompanyInfoProvider');
  }
  return context;
};

export const CompanyInfoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCompanyInfo = async () => {
    try {
      const response = await fetch('/api/company-info');
      if (response.ok) {
        const data = await response.json();
        setCompanyInfo(data);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCompanyInfo = async (data: Partial<CompanyInfo>) => {
    try {
      const response = await fetch('/api/company-info', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (response.ok) {
        const updatedData = await response.json();
        setCompanyInfo(updatedData);
      }
    } catch (error) {
      console.error('Error updating company info:', error);
      throw error;
    }
  };

  const refreshCompanyInfo = async () => {
    setLoading(true);
    await fetchCompanyInfo();
  };

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  return (
    <CompanyInfoContext.Provider
      value={{
        companyInfo,
        loading,
        updateCompanyInfo,
        refreshCompanyInfo,
      }}
    >
      {children}
    </CompanyInfoContext.Provider>
  );
};